import { doc, getDoc, setDoc, collection, addDoc, getDocs, orderBy, query, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase.js';
import { SEED } from '../../shared/seed-content.js';

export const BLOCKS = Object.keys(SEED);

// One in-flight request per block, so several sections of a page asking for
// the same document share a single read.
const pending = new Map();

// Firestore retries a failing read rather than rejecting -- against a project
// whose database does not exist yet, or a dead connection, getDoc simply never
// settles. The public site must not hang on that, so every read races a timer
// and falls back to the seed content.
// Generous enough to cover a cold Firestore handshake on a slow connection,
// since falling back to seed silently shows stale content rather than none.
const READ_TIMEOUT_MS = 8000;

function withTimeout(promise, fallback, label) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      console.warn(`${label} timed out after ${READ_TIMEOUT_MS}ms, using seed content.`);
      resolve(fallback);
    }, READ_TIMEOUT_MS);

    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => {
        clearTimeout(timer);
        console.warn(`${label} failed, using seed content.`, error);
        resolve(fallback);
      },
    );
  });
}

function withDefaults(id, data) {
  // A block saved before a field existed still renders: seed values fill the
  // gaps rather than leaving the page half-empty.
  return { ...SEED[id], ...(data ?? {}) };
}

export async function loadBlock(id) {
  if (!BLOCKS.includes(id)) throw new Error(`Unknown content block "${id}".`);
  if (pending.has(id)) return pending.get(id);

  const request = withTimeout(
    getDoc(doc(db, 'content', id)).then((snap) =>
      withDefaults(id, snap.exists() ? snap.data() : null),
    ),
    withDefaults(id, null),
    `content/${id}`,
  );

  pending.set(id, request);
  return request;
}

export async function loadBlocks(...ids) {
  const values = await Promise.all(ids.map(loadBlock));
  return Object.fromEntries(ids.map((id, i) => [id, values[i]]));
}

export async function saveBlock(id, data) {
  await setDoc(doc(db, 'content', id), { ...data, updatedAt: serverTimestamp() }, { merge: true });
  pending.set(id, Promise.resolve(withDefaults(id, data)));
}

export function invalidate(id) {
  if (id) pending.delete(id);
  else pending.clear();
}

/* ---------- contact inbox ---------- */

export function sendMessage({ name, contact, reason, message }) {
  return addDoc(collection(db, 'messages'), {
    name: String(name ?? '').trim(),
    contact: String(contact ?? '').trim(),
    reason: String(reason ?? '').trim(),
    message: String(message ?? '').trim(),
    done: false,
    createdAt: serverTimestamp(),
  });
}

export async function listMessages() {
  const snap = await getDocs(query(collection(db, 'messages'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => {
    const { createdAt, ...rest } = d.data();
    return { id: d.id, ...rest, createdAt: createdAt?.toMillis?.() ?? null };
  });
}

export function markMessageDone(id, done = true) {
  return updateDoc(doc(db, 'messages', id), { done });
}
